/**
 * Region and timeline audio playback engine for recorded sessions.
 *
 * Wraps the Web Audio API's `AudioBufferSourceNode` to provide transport
 * controls (play/pause/seek), selection looping, and variable playback
 * rates suitable for close acoustic/phonetic inspection of recordings.
 * Emits playhead position updates via a simple listener API so that a
 * waveform visualizer can stay in sync during playback.
 */
import { getFullAudio } from "../../storage/recordings.js";
/** Supported variable playback rates for phonetic inspection. */
export const PLAYBACK_RATES = [0.5, 0.75, 1.0];
const PLAYHEAD_POLL_MS = 33;
function pcm16ToFloat32(pcm) {
    const out = new Float32Array(pcm.length);
    for (let i = 0; i < pcm.length; i++) {
        out[i] = pcm[i] / 32768;
    }
    return out;
}
/**
 * Manages playback of a recording session's audio through the Web Audio
 * API, supporting seek, play/pause, selection looping, and variable
 * playback rate.
 */
export class AudioPlayer {
    constructor(ctx, events = {}) {
        Object.defineProperty(this, "ctx", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "buffer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "sourceNode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "gainNode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "state", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "idle"
        });
        Object.defineProperty(this, "rate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1.0
        });
        Object.defineProperty(this, "selection", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "loopSelection", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        /** Playback position (seconds) at the time playback was last
         *  started/paused, used to compute the live playhead. */
        Object.defineProperty(this, "startedAtCtxTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "startedAtOffset", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "playheadTimer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "events", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.ctx = ctx;
        this.events = events;
        this.gainNode = ctx.createGain();
        this.gainNode.connect(ctx.destination);
    }
    /** Current transport state. */
    getState() {
        return this.state;
    }
    /** Duration of the loaded buffer in seconds, or 0 if none loaded. */
    getDuration() {
        return this.buffer?.duration ?? 0;
    }
    /** Current playback rate. */
    getRate() {
        return this.rate;
    }
    /**
     * Load a full recording session's audio into an in-memory AudioBuffer.
     * Must complete before `play()` can be called.
     */
    async loadSession(session) {
        this.setState("loading");
        try {
            const pcm = await getFullAudio(session.id);
            const floatData = pcm16ToFloat32(pcm);
            const channels = session.channels || 1;
            const frameCount = Math.floor(floatData.length / channels);
            const buffer = this.ctx.createBuffer(channels, Math.max(frameCount, 1), session.sampleRate);
            for (let ch = 0; ch < channels; ch++) {
                const channelData = buffer.getChannelData(ch);
                if (channels === 1) {
                    channelData.set(floatData);
                }
                else {
                    for (let i = 0; i < frameCount; i++) {
                        channelData[i] = floatData[i * channels + ch];
                    }
                }
            }
            this.buffer = buffer;
            this.startedAtOffset = 0;
            this.setState("idle");
            console.info(`[audio-player] loaded session '${session.id}' ` +
                `duration=${buffer.duration.toFixed(2)}s`);
        }
        catch (err) {
            console.error("[audio-player] failed to load session", err);
            this.setState("idle");
            this.events.onError?.(err);
            throw err;
        }
    }
    /** Set the loop selection region; pass `null` to clear it. */
    setSelection(selection) {
        this.selection = selection;
    }
    /** Enable/disable looping of the current selection. */
    setLoopSelection(loop) {
        this.loopSelection = loop;
    }
    /**
     * Set the playback rate. Restarts the active source node in-place if
     * currently playing so the change takes effect immediately.
     */
    setRate(rate) {
        this.rate = rate;
        if (this.sourceNode) {
            this.sourceNode.playbackRate.value = rate;
            // Re-anchor position bookkeeping since rate change affects the
            // relationship between context time and playback offset.
            const pos = this.getPlayheadPosition();
            this.startedAtOffset = pos;
            this.startedAtCtxTime = this.ctx.currentTime;
        }
    }
    /** Current playhead position in seconds. */
    getPlayheadPosition() {
        if (this.state !== "playing") {
            return this.startedAtOffset;
        }
        const elapsed = (this.ctx.currentTime - this.startedAtCtxTime) * this.rate;
        return this.startedAtOffset + elapsed;
    }
    /** Seek to an absolute position in seconds, without changing state. */
    seek(positionSec) {
        const wasPlaying = this.state === "playing";
        if (wasPlaying)
            this.stopSourceNode();
        this.startedAtOffset = this.clampPosition(positionSec);
        this.startedAtCtxTime = this.ctx.currentTime;
        if (wasPlaying)
            this.startSourceNode(this.startedAtOffset);
        this.events.onPlayhead?.(this.startedAtOffset);
    }
    /** Begin (or resume) playback from the current playhead position. */
    async play() {
        if (!this.buffer) {
            throw new Error("[audio-player] no audio loaded");
        }
        if (this.ctx.state === "suspended") {
            await this.ctx.resume();
        }
        if (this.state === "playing")
            return;
        const startAt = this.selection && this.startedAtOffset < this.selection.startSec
            ? this.selection.startSec
            : this.startedAtOffset;
        this.startSourceNode(startAt);
        this.startedAtOffset = startAt;
        this.startedAtCtxTime = this.ctx.currentTime;
        this.setState("playing");
        this.startPlayheadTimer();
    }
    /** Pause playback, preserving the current playhead position. */
    pause() {
        if (this.state !== "playing")
            return;
        const pos = this.getPlayheadPosition();
        this.stopSourceNode();
        this.startedAtOffset = this.clampPosition(pos);
        this.stopPlayheadTimer();
        this.setState("paused");
        this.events.onPlayhead?.(this.startedAtOffset);
    }
    /** Stop playback entirely and reset the playhead to the start. */
    stop() {
        this.stopSourceNode();
        this.stopPlayheadTimer();
        this.startedAtOffset = this.selection?.startSec ?? 0;
        this.setState("idle");
        this.events.onPlayhead?.(this.startedAtOffset);
    }
    /** Tear down the player and release audio graph resources. */
    dispose() {
        this.stopSourceNode();
        this.stopPlayheadTimer();
        try {
            this.gainNode.disconnect();
        }
        catch {
            // ignore
        }
        this.buffer = null;
        this.setState("idle");
    }
    // -- internals -----------------------------------------------------
    clampPosition(positionSec) {
        const duration = this.getDuration();
        return Math.max(0, Math.min(positionSec, duration));
    }
    startSourceNode(offsetSec) {
        if (!this.buffer)
            return;
        this.stopSourceNode();
        const node = this.ctx.createBufferSource();
        node.buffer = this.buffer;
        node.playbackRate.value = this.rate;
        node.connect(this.gainNode);
        const selection = this.selection;
        const loop = this.loopSelection && !!selection;
        if (loop && selection) {
            node.loop = true;
            node.loopStart = selection.startSec;
            node.loopEnd = selection.endSec;
        }
        node.onended = () => {
            if (this.sourceNode !== node)
                return;
            this.sourceNode = null;
            if (!loop) {
                this.stopPlayheadTimer();
                this.setState("idle");
                this.startedAtOffset = 0;
                this.events.onEnded?.();
            }
        };
        const endBoundary = selection && !loop ? Math.max(0, selection.endSec - offsetSec) : undefined;
        if (endBoundary !== undefined) {
            node.start(0, offsetSec, endBoundary);
        }
        else {
            node.start(0, offsetSec);
        }
        this.sourceNode = node;
    }
    stopSourceNode() {
        if (this.sourceNode) {
            try {
                this.sourceNode.onended = null;
                this.sourceNode.stop();
                this.sourceNode.disconnect();
            }
            catch {
                // ignore: node may already be stopped
            }
            this.sourceNode = null;
        }
    }
    startPlayheadTimer() {
        this.stopPlayheadTimer();
        this.playheadTimer = setInterval(() => {
            const pos = this.getPlayheadPosition();
            const duration = this.getDuration();
            if (this.selection && this.loopSelection) {
                if (pos >= this.selection.endSec) {
                    // Loop handled natively by AudioBufferSourceNode; just
                    // report position wrapped for visualizer consistency.
                    this.events.onPlayhead?.(this.selection.startSec);
                    return;
                }
            }
            else if (pos >= duration) {
                this.stopPlayheadTimer();
                this.setState("idle");
                this.startedAtOffset = 0;
                this.events.onEnded?.();
                return;
            }
            this.events.onPlayhead?.(pos);
        }, PLAYHEAD_POLL_MS);
    }
    stopPlayheadTimer() {
        if (this.playheadTimer !== null) {
            clearInterval(this.playheadTimer);
            this.playheadTimer = null;
        }
    }
    setState(state) {
        if (this.state === state)
            return;
        this.state = state;
        this.events.onStateChange?.(state);
    }
}
